import {
  BackEndValueType,
  ECalculatedFieldFunction,
  IDatabaseTableModel,
  IGenericSelectCalculatedField,
  IGenericSelectTablesModel,
  IGenericSelectWhereConditionModel
} from "@kuika/kuika-cl-model";
import {
  EManagedDbUpdateWhereConditionOperator,
  ESymbolCategory,
  ISymbol
} from "@kuika/kuika-cl-model/src/models/builder.dtos";
import classNames from "classnames";
import _ from "lodash";
import * as React from "react";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { SymbolPicker } from "../../../right-panel/properties-panel/symbol-picker";
import { getTableColumnWidth } from "./filter-column-width";
import { getParamSymbolHoverText } from "./param-symbol-hover-text";

export interface IFilterSelectGroupState {}

export interface IFilterSelectGroupProps {
  type: "and" | "or";
  tables: IDatabaseTableModel[];
  selectedTables: IGenericSelectTablesModel[];
  calculatedFields: IGenericSelectCalculatedField[];
  data: IGenericSelectWhereConditionModel;
  updateData: (newData: IGenericSelectWhereConditionModel) => void;
  hasMoreData: boolean;
  delete: () => void;
  getTypeIcon: (type: string) => React.ReactElement;
}

interface IFilterSelectGroupTableRow {
  id: string;
  tableName: string;
  colName: string;
  conditionOperator: EManagedDbUpdateWhereConditionOperator;
  paramSymbol: ISymbol;
  data: IGenericSelectWhereConditionModel;
}

class FilterSelectGroup extends React.PureComponent<IFilterSelectGroupProps, IFilterSelectGroupState> {
  private static readonly CALCULATED_FIELDS_TABLE_KEY = "__calculated_fields__";

  constructor(props: IFilterSelectGroupProps) {
    super(props);
    this.state = {};
  }

  supportsFilter = (func?: ECalculatedFieldFunction): boolean => {
    if (func === ECalculatedFieldFunction.RowNumber) return false;
    if (func === ECalculatedFieldFunction.DateDiff) return true;
    return false;
  };

  getFilterableCalculatedFields = () => {
    return (this.props.calculatedFields || []).filter(
      (x) => x.alias && x.alias.trim() !== "" && this.supportsFilter(x.function)
    );
  };

  isCalculatedFieldsTableSelected = () => {
    const dataAny = this.props.data as any;
    return (
      dataAny?.isCalculatedField === true || this.props.data.tableName === FilterSelectGroup.CALCULATED_FIELDS_TABLE_KEY
    );
  };

  handleTableSelect = (selectedValue: string) => {
    const data = _.cloneDeep(this.props.data);
    const dataAny = data as any;
    if (selectedValue === FilterSelectGroup.CALCULATED_FIELDS_TABLE_KEY) {
      data.tableName = FilterSelectGroup.CALCULATED_FIELDS_TABLE_KEY;
      data.tableAlias = "";
      data.tableSchema = "";
      data.tableId = undefined;
      data.colName = "";
      data.colSqlDataType = "";
      data.paramSymbol = { category: ESymbolCategory.fixed } as ISymbol;
      dataAny.isCalculatedField = true;
      dataAny.calculatedFieldAlias = "";
      this.props.updateData(data);
      return;
    }
    // Find the selected table by alias or name
    const selectedTable = this.props.selectedTables.find((x) => (x.alias || x.name) === selectedValue);
    if (selectedTable) {
      data.tableName = selectedTable.name;
      data.tableAlias = selectedTable.alias;
      data.tableSchema = this.props.tables.find((x) => x.name === selectedTable.name)?.schema ?? "";
      data.tableId = this.props.tables.find((x) => x.name === selectedTable.name)?.id;
    }
    data.colName = "";
    data.colSqlDataType = "";
    data.paramSymbol = { category: ESymbolCategory.fixed } as ISymbol;
    dataAny.isCalculatedField = false;
    dataAny.calculatedFieldAlias = "";
    this.props.updateData(data);
  };

  // Get the display value for the table dropdown (alias if exists, otherwise name)
  getTableDisplayValue = () => {
    const { data } = this.props;
    if (data.tableName === FilterSelectGroup.CALCULATED_FIELDS_TABLE_KEY) {
      return "Calculated Fields";
    }
    if (data.tableAlias) {
      return data.tableAlias;
    }
    // Find if this table has an alias in selectedTables
    const selectedTable = this.props.selectedTables.find((x) => x.name === data.tableName);
    return selectedTable?.alias || data.tableName;
  };

  handleColumnSelect = (colName: string) => {
    const data = _.cloneDeep(this.props.data);
    const dataAny = data as any;
    if (this.isCalculatedFieldsTableSelected()) {
      const calcField = this.getFilterableCalculatedFields().find((x) => x.alias === colName);
      if (calcField) {
        data.tableName = FilterSelectGroup.CALCULATED_FIELDS_TABLE_KEY;
        data.tableAlias = "";
        data.tableSchema = "";
        data.tableId = undefined;
        data.colName = calcField.alias;
        // DateDiff returns an integer; use int32 for filter input types
        data.colSqlDataType = "int32";
        data.paramSymbol = { category: ESymbolCategory.fixed } as ISymbol;
        dataAny.isCalculatedField = true;
        dataAny.calculatedFieldAlias = calcField.alias;
        this.props.updateData(data);
        return;
      }
    }
    data.colName = colName;
    data.colSqlDataType = "";
    const selectedTable = this.props.tables.find((x) => x.name === data.tableName);
    if (selectedTable) {
      const selectedColumn = selectedTable.columns.find((x) => x.name === colName);
      if (selectedColumn) {
        data.colSqlDataType = selectedColumn.sqlDataType;
      }
    }
    data.paramSymbol = { category: ESymbolCategory.fixed } as ISymbol;
    if (data.colSqlDataType?.toLocaleLowerCase() === "boolean" || data.colSqlDataType?.toLocaleLowerCase() === "bit") {
      data.paramSymbol.fixedValue = false;
    }
    dataAny.isCalculatedField = false;
    dataAny.calculatedFieldAlias = "";
    this.props.updateData(data);
  };

  handleConditionOperatorSelect = (value: EManagedDbUpdateWhereConditionOperator) => {
    const data = _.cloneDeep(this.props.data);
    data.conditionOperator = value;
    this.props.updateData(data);
  };

  getSelectedTableColumns = (tableName: string) => {
    const targetTable = this.props.tables.find((x) => x.name === tableName);
    return targetTable?.columns ?? [];
  };

  handleSymbolSelected = (symbol: ISymbol) => {
    const data = _.cloneDeep(this.props.data);
    data.paramSymbol = _.cloneDeep(symbol);
    this.props.updateData(data);
  };

  findKuikaTypeOfColumn = (tableName, columnName): BackEndValueType | any => {
    if (tableName === FilterSelectGroup.CALCULATED_FIELDS_TABLE_KEY) {
      return "int32";
    }
    const targetTable = this.props.tables.find((x) => x.name === tableName);

    const targetColumn = targetTable?.columns.find((x) => x.name === columnName);

    return targetColumn?.kuikaDataType;
  };

  /** Long names truncate, so every label carries a tooltip with the full text. */
  renderOptionLabel = (icon: React.ReactElement, text: string): React.ReactElement => {
    return (
      <KuikaTooltip content={text}>
        <div className="filter-select-group__option">
          {icon}
          {/* Not KuikaText: it stamps `title`, drawing a second native tooltip. */}
          <span className="kuika-typography-text">{text}</span>
        </div>
      </KuikaTooltip>
    );
  };

  getTableOptions = () => {
    const tableOptions = this.props.selectedTables.map((x) => ({
      value: x.alias || x.name,
      label: this.renderOptionLabel(<KuikaSvg name="table" />, x.alias || x.name),
      searchLabel: x.alias || x.name
    }));

    // Add calculated fields option if there are filterable calculated fields
    const filterableCalculatedFields = this.getFilterableCalculatedFields();
    if (filterableCalculatedFields.length > 0) {
      tableOptions.push({
        value: FilterSelectGroup.CALCULATED_FIELDS_TABLE_KEY,
        label: this.renderOptionLabel(<KuikaSvg name="function" />, "Calculated Fields"),
        searchLabel: "Calculated Fields"
      });
    }

    return tableOptions;
  };

  getColumnOptions = () => {
    if (this.isCalculatedFieldsTableSelected()) {
      return this.getFilterableCalculatedFields().map((x) => {
        const iconName = x.function === ECalculatedFieldFunction.DateDiff ? "calendar-event" : "layout-rows";
        const funcName = x.function === ECalculatedFieldFunction.DateDiff ? "DateDiff" : "RowNumber";
        return {
          value: x.alias,
          label: this.renderOptionLabel(<KuikaSvg name={iconName} />, `${funcName}: ${x.alias}`),
          searchLabel: `${funcName} ${x.alias}`
        };
      });
    }

    return this.getSelectedTableColumns(this.props.data.tableName).map((x) => ({
      value: x.name,
      label: this.renderOptionLabel(this.props.getTypeIcon(x.kuikaDataType), x.name),
      searchLabel: x.name
    }));
  };

  getConditionOptions = () => {
    const conditions = [
      { value: EManagedDbUpdateWhereConditionOperator.isEqualTo, label: "Is Equal To" },
      { value: EManagedDbUpdateWhereConditionOperator.isNotEqualTo, label: "Is Not Equal To" },
      { value: EManagedDbUpdateWhereConditionOperator.startsWith, label: "Starts With" },
      { value: EManagedDbUpdateWhereConditionOperator.doesNotStartWith, label: "Does Not Start With" },
      { value: EManagedDbUpdateWhereConditionOperator.contains, label: "Contains" },
      { value: EManagedDbUpdateWhereConditionOperator.doesNotContain, label: "Does Not Contain" },
      { value: EManagedDbUpdateWhereConditionOperator.endsWith, label: "Ends With" },
      { value: EManagedDbUpdateWhereConditionOperator.doesNotEndtWith, label: "Does Not End With" },
      { value: EManagedDbUpdateWhereConditionOperator.isLessThan, label: "Is Less Than" },
      { value: EManagedDbUpdateWhereConditionOperator.isLessOrEqual, label: "Is Less Or Equal" },
      { value: EManagedDbUpdateWhereConditionOperator.isGraeterThan, label: "Is Greater Than" },
      { value: EManagedDbUpdateWhereConditionOperator.isGreaterOrEqual, label: "Is Greater Or Equal" }
    ];

    return conditions.map((condition) => ({
      value: condition.value,
      label: this.renderOptionLabel(<KuikaSvg name="filter" />, condition.label),
      searchLabel: condition.label
    }));
  };

  renderValidationIcon = () => {
    return <KuikaSvg name="alert-circle" width={16} height={16} color="var(--red-5)" style={{ flexShrink: 0 }} />;
  };

  getValidationSuffixIcon = (hasError: boolean, hasValue: boolean) => {
    if (hasError && !hasValue) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <KuikaSvg name="alert-circle" width={16} height={16} color="var(--red-5)" />
          <KuikaSvg name="chevron-down" type="arrows" width={12} height={12} color="rgba(96, 111, 133, 1)" />
        </div>
      );
    }
    return undefined;
  };

  getTableColumns = (): KuikaTableColumn<IFilterSelectGroupTableRow>[] => {
    const { data } = this.props;
    const tableOptions = this.getTableOptions();
    const tableColumnWidth: number = getTableColumnWidth(tableOptions.map((option) => option.searchLabel));

    return [
      {
        id: "tableName",
        header: () => "Table",
        // Sized to its options so the slack goes to Column. See filter-column-width.ts.
        width: tableColumnWidth,
        cell: () => {
          const hasValidationError = data.validations?.tableName === false;
          return (
            <div
              className={classNames("filter-select", { "has-validation-error": hasValidationError })}
              data-testid="filterConditionTableSelect"
            >
              <KuikaSelectbox
                className="filter-select-group__select kuika-selectbox--no-border"
                searchable
                value={data.tableName}
                options={tableOptions}
                onChange={(value) => this.handleTableSelect(value as string)}
                placeholder="Select table"
                overlayClassName="filter-select-group__selectbox-overlay"
                suffixIcon={this.getValidationSuffixIcon(hasValidationError, !!data.tableName)}
              />
            </div>
          );
        }
      },
      {
        id: "colName",
        header: () => "Column",
        // Deliberately width-less: the only flexible column, holding the longest labels. It narrows no
        // further than Table — never under TABLE_COLUMN_MIN_WIDTH, which a short name like "Id" fits —
        // and past that the row scrolls sideways instead of cutting "Id" down to "I".
        minWidth: tableColumnWidth,
        cell: () => {
          const hasValidationError = data.validations?.colName === false;
          return (
            <div
              className={classNames("filter-select", { "has-validation-error": hasValidationError })}
              data-testid="filterConditionColumnSelect"
            >
              <KuikaSelectbox
                className="filter-select-group__select kuika-selectbox--no-border"
                value={data.colName}
                options={this.getColumnOptions()}
                onChange={(value) => this.handleColumnSelect(value as string)}
                placeholder="Select column"
                searchable
                overlayClassName="filter-select-group__selectbox-overlay"
                suffixIcon={this.getValidationSuffixIcon(hasValidationError, !!data.colName)}
              />
            </div>
          );
        }
      },
      {
        id: "conditionOperator",
        header: () => "Condition",
        // Numeric (KuikaTable ignores % widths). A floor: what separates "Does Not
        // Start With" from "Does Not End With" is at the end, so truncating misleads.
        width: 200,
        cell: () => {
          const hasValidationError = data.validations?.conditionOperator === false;
          return (
            <div className="filter-select" data-testid="filterConditionOperatorSelect">
              <KuikaSelectbox
                className="filter-select-group__select kuika-selectbox--no-border"
                searchable
                value={data.conditionOperator}
                options={this.getConditionOptions()}
                onChange={(value) =>
                  this.handleConditionOperatorSelect(value as EManagedDbUpdateWhereConditionOperator)
                }
                placeholder="Select condition"
                overlayClassName="filter-select-group__selectbox-overlay"
                suffixIcon={this.getValidationSuffixIcon(hasValidationError, !!data.conditionOperator)}
              />
            </div>
          );
        }
      },
      {
        id: "paramSymbol",
        header: () => "Value",
        // Wide enough for the longest User value a filter binds, "User Current Username" (190px
        // in Inter, the bound label's 12px/500). A longer binding ellipsises and keeps its tooltip.
        width: 190,
        cell: () => {
          const hasValidationError = data.validations?.paramSymbol === false;
          return (
            // On the cell, not the input, which fills only part of it. `keepMounted`
            // is required: the swap would remount SymbolPicker mid-interaction.
            <KuikaTooltip content={getParamSymbolHoverText(data.paramSymbol)} keepMounted>
              <div
                className={classNames("filter-select-group__symbol-picker", {
                  "has-validation-error": hasValidationError
                })}
                /* `filterConditionValueCell`, not `…ValueSymbolPicker`: the SymbolPicker below is
                   single-line with no pickerIcon, so SymbolPickerCascader renders its wand as
                   `<KuikaSvg data-testid={`${data_testid}SymbolPicker`}>` — literally
                   `filterConditionValueSymbolPicker`. This wrapper carried the same id, so ONE id
                   addressed two nested elements and every scoped lookup was a strict-mode
                   violation (QA-484, scenario 413). getTestIdBase's wand exclusion cannot prevent
                   that: it only suppresses the picker's own span, and knows nothing about an id a
                   caller stamps on its wrapper. */
                data-testid="filterConditionValueCell"
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <div style={{ flex: 1 }}>
                  <SymbolPicker
                    key={`${data.tableName}-${data.colName}-${data.conditionOperator}`}
                    symbol={data.paramSymbol}
                    valueType={this.findKuikaTypeOfColumn(data.tableName, data.colName)}
                    onSelectedSymbol={this.handleSymbolSelected}
                    hideActionResults
                    showInSingleLine
                    isGenericSelect
                    /* Without this the picker reaches SymbolPickerCascader with no name at all: it
                       rendered a literal `undefinedSymbolPicker` node, its popup search fell back to
                       the shared un-prefixed `symbolPickerPopupSearch`, and no `kcascader-trigger-*`
                       class was emitted, so the filter Value cell had no stable handle of its own. */
                    data_testid="filterConditionValue"
                  />
                </div>
                {hasValidationError && this.renderValidationIcon()}
              </div>
            </KuikaTooltip>
          );
        }
      },
      {
        id: "actions",
        header: () => "",
        width: 48,
        cell: () => (
          <div className="filter-select-group__delete-icon" onClick={() => this.props.delete()}>
            <KuikaSvg name="trash" pointer width={16} height={16} color="var(--red-5)" />
          </div>
        ),
        noWrap: true
      }
    ];
  };

  render() {
    const { data } = this.props;
    const calculatedFields = this.getFilterableCalculatedFields();

    const tableData: IFilterSelectGroupTableRow[] = [
      {
        id: "1",
        tableName: data.tableName,
        colName: data.colName,
        conditionOperator: data.conditionOperator,
        paramSymbol: data.paramSymbol,
        data
      }
    ];

    return (
      <div
        // A Select Action can hold several condition rows (AND rows plus OR-group
        // members), so every row carries the same id and the cell ids below repeat
        // per row. Consumers address one row first, then look up the cell inside it.
        data-testid="filterConditionRow"
        className={classNames(
          "filter-select-group-wrapper",
          { "filter-select-group__with-svg": this.props.type === "and" && this.props.hasMoreData },
          { "filter-or-group__with-svg": this.props.type === "or" && this.props.hasMoreData },
          {
            "has-validation-error":
              data?.validations && Object.values(data?.validations as any).some((v) => v === false)
          }
        )}
      >
        <KuikaTable
          data={tableData}
          columns={this.getTableColumns()}
          rowKey={(row) => row.id}
          enablePagination={false}
          showHeader={false}
          // Past every column's floor the row scrolls sideways inside its frame. The selectboxes,
          // the symbol picker's popup and the tooltips all mount outside the table, so the
          // scroller clips none of them.
          horizontalScroll
          // One condition row, not a list: without this the kit pads every row's frame out to 400px.
          fitContentHeight
        />
      </div>
    );
  }
}

export const FilterSelectGroupComponent = FilterSelectGroup;
